import React, { useCallback, useEffect, useState } from "react";
import {
  savedPromptsService,
  type EnhancePromptResult,
  type SavedPrompt,
} from "../services/savedPromptsService";

type PromptLibraryDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelectPrompt: (text: string) => void;
};

type View = "list" | "add";
type GenerationStep = "input" | "generating" | "review";

export function PromptLibraryDialog({ isOpen, onClose, onSelectPrompt }: PromptLibraryDialogProps) {
  const [view, setView] = useState<View>("list");
  const [searchQuery, setSearchQuery] = useState("");
  const [prompts, setPrompts] = useState<SavedPrompt[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [listError, setListError] = useState("");

  const [instruction, setInstruction] = useState("");
  const [generatedPrompt, setGeneratedPrompt] = useState("");
  const [promptTitle, setPromptTitle] = useState("");
  const [generationStep, setGenerationStep] = useState<GenerationStep>("input");
  const [enhanceData, setEnhanceData] = useState<EnhancePromptResult | null>(null);
  const [actionError, setActionError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const loadPrompts = useCallback(async () => {
    setIsLoading(true);
    setListError("");
    try {
      const list = await savedPromptsService.list(100);
      setPrompts(list);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to load prompts";
      setListError(msg);
      setPrompts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetAddState = useCallback((goToList: boolean) => {
    setInstruction("");
    setGeneratedPrompt("");
    setPromptTitle("");
    setGenerationStep("input");
    setEnhanceData(null);
    setActionError("");
    if (goToList) setView("list");
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    void loadPrompts();
  }, [isOpen, loadPrompts]);

  useEffect(() => {
    if (isOpen) return;
    setView("list");
    setSearchQuery("");
    setActionError("");
    setListError("");
    resetAddState(false);
  }, [isOpen, resetAddState]);

  const handleGeneratePrompt = async () => {
    if (!instruction.trim()) return;
    setGenerationStep("generating");
    setActionError("");
    try {
      const data = await savedPromptsService.enhance(instruction.trim());
      setGeneratedPrompt(data.enhancedPrompt);
      setPromptTitle(data.title || "");
      setEnhanceData(data);
      setGenerationStep("review");
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to enhance prompt";
      setActionError(msg);
      setGenerationStep("input");
    }
  };

  const handleSavePrompt = async () => {
    if (!promptTitle.trim() || !generatedPrompt.trim()) return;
    setIsSaving(true);
    setActionError("");
    try {
      await savedPromptsService.create({
        title: promptTitle.trim(),
        content: generatedPrompt.trim(),
        sourcePrompt: enhanceData?.sourcePrompt || instruction,
        metadata: {
          improvementNotes: enhanceData?.improvementNotes,
          tokensUsed: enhanceData?.tokensUsed,
          modelUsed: enhanceData?.modelUsed,
        },
      });
      resetAddState(true);
      await loadPrompts();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to save prompt";
      setActionError(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePrompt = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setActionError("");
    try {
      await savedPromptsService.delete(id);
      setPrompts((prev) => prev.filter((p) => p.id !== id));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to delete";
      setActionError(msg);
    }
  };

  const filteredPrompts = prompts.filter(
    (p) =>
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="prompt-lib-overlay" role="dialog" aria-modal="true" aria-labelledby="prompt-lib-title">
      <button type="button" className="prompt-lib-backdrop" aria-label="Close" onClick={onClose} />
      <div className="prompt-lib-panel">
        <header className="prompt-lib-header">
          <div className="prompt-lib-header-row">
            {view === "add" && (
              <button
                type="button"
                className="ghost-btn prompt-lib-back"
                onClick={() => resetAddState(true)}
                aria-label="Back to list"
              >
                ←
              </button>
            )}
            <div className="prompt-lib-header-text">
              <h2 id="prompt-lib-title" className="prompt-lib-title">
                {view === "list" ? "Prompt Library" : "Create smart prompt"}
              </h2>
              <p className="prompt-lib-subtitle">
                {view === "list"
                  ? "Save and reuse your favorite AI instructions"
                  : "Describe what you need; AI will craft a reusable prompt."}
              </p>
            </div>
            <button type="button" className="ghost-btn prompt-lib-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </header>

        {actionError ? <p className="prompt-lib-banner-error">{actionError}</p> : null}
        {listError && view === "list" ? <p className="prompt-lib-banner-error">{listError}</p> : null}

        <div className="prompt-lib-body">
          {view === "list" ? (
            <>
              <div className="prompt-lib-toolbar">
                <input
                  type="search"
                  placeholder="Search prompts…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="prompt-lib-search"
                />
                <button type="button" className="primary-btn prompt-lib-new" onClick={() => setView("add")}>
                  + New
                </button>
              </div>
              <div className="prompt-lib-scroll">
                {isLoading ? (
                  <p className="prompt-lib-muted">Loading prompts…</p>
                ) : filteredPrompts.length > 0 ? (
                  <ul className="prompt-lib-list">
                    {filteredPrompts.map((p) => (
                      <li key={p.id}>
                        <div
                          role="button"
                          tabIndex={0}
                          className="prompt-lib-card"
                          onClick={() => {
                            onSelectPrompt(p.content);
                            onClose();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onSelectPrompt(p.content);
                              onClose();
                            }
                          }}
                        >
                          <div className="prompt-lib-card-head">
                            <span className="prompt-lib-card-title">{p.title}</span>
                            <button
                              type="button"
                              className="prompt-lib-delete"
                              title="Delete"
                              onClick={(e) => void handleDeletePrompt(p.id, e)}
                            >
                              ×
                            </button>
                          </div>
                          <p className="prompt-lib-card-preview">{p.content}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="prompt-lib-empty">
                    <p>No prompts found</p>
                    <p className="prompt-lib-muted">
                      {searchQuery ? "Try a different search" : "Create your first prompt"}
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="prompt-lib-add">
              <div className={`prompt-lib-step ${generationStep !== "input" ? "prompt-lib-step-dim" : ""}`}>
                <label className="prompt-lib-label">
                  <span className="prompt-lib-step-num">1</span> What do you want to achieve?
                </label>
                <textarea
                  className="prompt-lib-textarea"
                  placeholder="e.g. Summarize NDAs with focus on non-compete and confidentiality…"
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  disabled={generationStep !== "input"}
                  rows={4}
                />
                {generationStep === "input" ? (
                  <button
                    type="button"
                    className="primary-btn prompt-lib-full"
                    disabled={!instruction.trim()}
                    onClick={() => void handleGeneratePrompt()}
                  >
                    ✦ Generate professional prompt
                  </button>
                ) : null}
              </div>

              {generationStep === "generating" ? (
                <div className="prompt-lib-step">
                  <p className="prompt-lib-muted">Structuring your prompt…</p>
                </div>
              ) : null}

              {generationStep === "review" ? (
                <>
                  <div className="prompt-lib-step">
                    <label className="prompt-lib-label">
                      <span className="prompt-lib-step-num">2</span> Review & edit
                    </label>
                    <textarea
                      className="prompt-lib-textarea"
                      value={generatedPrompt}
                      onChange={(e) => setGeneratedPrompt(e.target.value)}
                      rows={6}
                    />
                  </div>
                  <div className="prompt-lib-step">
                    <label className="prompt-lib-label">
                      <span className="prompt-lib-step-num">3</span> Name your prompt
                    </label>
                    <input
                      type="text"
                      value={promptTitle}
                      onChange={(e) => setPromptTitle(e.target.value)}
                      placeholder="e.g. NDA summary"
                    />
                  </div>
                  <button
                    type="button"
                    className="primary-btn prompt-lib-full"
                    disabled={!promptTitle.trim() || !generatedPrompt.trim() || isSaving}
                    onClick={() => void handleSavePrompt()}
                  >
                    {isSaving ? "Saving…" : "Save to library"}
                  </button>
                </>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
